import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Button,
    DatePicker,
    Popconfirm,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import EventFormModal from './EventFormModal';
import { deleteEvent, listEvents } from './eventsService';
import { fetchTeamInsights } from '../teamInsightsService';
import {
    EventType,
    EVENT_TYPES,
    EVENT_TYPE_LABELS,
    TeamMemberEvent,
    TeamMemberInsight,
} from '../types';

dayjs.extend(utc);

const { RangePicker } = DatePicker;

const defaultRange = (): [Dayjs, Dayjs] => {
    const end = dayjs.utc().subtract(1, 'day');
    const start = end.startOf('month');
    return [start, end];
};

const MemberEventsDashboard: React.FC = () => {
    const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange());
    const [loginFilter, setLoginFilter] = useState<string | undefined>();
    const [typeFilter, setTypeFilter] = useState<EventType | undefined>();
    const [events, setEvents] = useState<TeamMemberEvent[]>([]);
    const [members, setMembers] = useState<Array<Pick<TeamMemberInsight, 'login' | 'avatarUrl'>>>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<TeamMemberEvent | null>(null);

    const fromYmd = useMemo(() => range[0].format('YYYY-MM-DD'), [range]);
    const toYmd = useMemo(() => range[1].format('YYYY-MM-DD'), [range]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [list, insights] = await Promise.all([
                listEvents({ from: fromYmd, to: toYmd, login: loginFilter, type: typeFilter }),
                // Reuse the existing endpoint to populate the member picker (login + avatar).
                fetchTeamInsights({
                    from: range[0].startOf('day').toISOString(),
                    to: range[1].endOf('day').toISOString(),
                }),
            ]);
            setEvents(list);
            setMembers(insights.members.map((m) => ({ login: m.login, avatarUrl: m.avatarUrl })));
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Unknown error');
        } finally {
            setLoading(false);
        }
    }, [fromYmd, toYmd, loginFilter, typeFilter, range]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        document.title = 'Member Events · Sonat Support';
    }, []);

    const handleDelete = async (id: string) => {
        try {
            await deleteEvent(id);
            message.success('Deleted');
            await load();
        } catch (err: any) {
            message.error(err?.response?.data?.message || err?.message || 'Delete failed');
        }
    };

    const columns: ColumnsType<TeamMemberEvent> = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: 120,
            sorter: (a, b) => a.date.localeCompare(b.date),
            defaultSortOrder: 'descend',
        },
        {
            title: 'Member',
            dataIndex: 'login',
            key: 'login',
            render: (login: string) => {
                const m = members.find((x) => x.login.toLowerCase() === login.toLowerCase());
                return (
                    <Space>
                        <Avatar src={m?.avatarUrl} size="small">
                            {login.slice(0, 1).toUpperCase()}
                        </Avatar>
                        {login}
                    </Space>
                );
            },
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: 160,
            render: (t: string) => <Tag>{EVENT_TYPE_LABELS[t as EventType] ?? t}</Tag>,
        },
        {
            title: 'Note',
            dataIndex: 'note',
            key: 'note',
            render: (note: string) => (note ? <span>{note}</span> : <Typography.Text type="secondary">—</Typography.Text>),
        },
        {
            title: '',
            key: 'actions',
            width: 160,
            render: (_, row) => (
                <Space>
                    <Button size="small" onClick={() => { setEditing(row); setModalOpen(true); }}>
                        Edit
                    </Button>
                    <Popconfirm title="Delete this event?" onConfirm={() => handleDelete(row.id)} okText="Delete" okType="danger">
                        <Button size="small" danger>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 16 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                <Typography.Title level={3} style={{ margin: 0 }}>
                    Member events
                </Typography.Title>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => { setEditing(null); setModalOpen(true); }}
                >
                    Add event
                </Button>
            </Space>

            <Space wrap style={{ marginBottom: 16 }}>
                <RangePicker
                    value={range}
                    onChange={(dates) => {
                        if (dates && dates[0] && dates[1]) setRange([dates[0], dates[1]]);
                    }}
                    allowClear={false}
                />
                <Select
                    allowClear
                    placeholder="Filter by member"
                    style={{ minWidth: 200 }}
                    value={loginFilter}
                    onChange={(v) => setLoginFilter(v)}
                    options={members.map((m) => ({ value: m.login, label: m.login }))}
                    showSearch
                />
                <Select
                    allowClear
                    placeholder="Filter by type"
                    style={{ minWidth: 200 }}
                    value={typeFilter}
                    onChange={(v) => setTypeFilter(v)}
                    options={EVENT_TYPES.map((t) => ({ value: t, label: EVENT_TYPE_LABELS[t] }))}
                />
            </Space>

            {error && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={error} />}

            <Table
                rowKey="id"
                loading={loading}
                dataSource={events}
                columns={columns}
                pagination={false}
            />

            <EventFormModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSaved={load}
                members={members}
                editing={editing}
            />
        </div>
    );
};

export default MemberEventsDashboard;
